<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>Managed SQL Server Instances (Admin View)</h2>
        <vs-button color="success" @click="loadInstances()">
          <feather-icon icon="RefreshCwIcon" class="mr-2"></feather-icon>
          Refresh
        </vs-button>
      </div>

      <!-- Stats Cards -->
      <vs-row class="mb-4" v-if="stats">
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-primary">{{ stats.total }}</h3>
            <small>Total</small>
          </vx-card>
        </vs-col>
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-success">{{ stats.running }}</h3>
            <small>Running</small>
          </vx-card>
        </vs-col>
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-warning">{{ stats.suspended }}</h3>
            <small>Suspended</small>
          </vx-card>
        </vs-col>
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-danger">{{ stats.failed }}</h3>
            <small>Failed</small>
          </vx-card>
        </vs-col>
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-info">{{ stats.with_dr }}</h3>
            <small>With DR</small>
          </vx-card>
        </vs-col>
        <vs-col vs-w="2" vs-sm="4" vs-xs="6">
          <vx-card class="text-center">
            <h3 class="text-grey">{{ formatPrice(stats.billing && stats.billing.pending_cost ? stats.billing.pending_cost : 0) }}</h3>
            <small>Pending Cost</small>
          </vx-card>
        </vs-col>
      </vs-row>

      <!-- Filters -->
      <vs-row class="mb-4">
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.instance_id"
            class="w-full"
            label-placeholder="Instance UUID"
            @keyup.enter="loadInstances()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="2" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.user_id"
            class="w-full"
            label-placeholder="User ID"
            @keyup.enter="loadInstances()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="2" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.status"
            class="w-full"
            placeholder="Status"
            @change="loadInstances()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item value="running" text="Running"></vs-select-item>
            <vs-select-item value="stopped" text="Stopped"></vs-select-item>
            <vs-select-item value="provisioning" text="Provisioning"></vs-select-item>
            <vs-select-item value="suspended" text="Suspended"></vs-select-item>
            <vs-select-item value="failed" text="Failed"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.billing_mode"
            class="w-full"
            placeholder="Billing Mode"
            @change="loadInstances()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item value="hourly" text="Hourly"></vs-select-item>
            <vs-select-item value="monthly" text="Monthly"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.role"
            class="w-full"
            placeholder="Role"
            @change="loadInstances()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item value="standalone" text="Standalone"></vs-select-item>
            <vs-select-item value="write" text="Primary (Write)"></vs-select-item>
            <vs-select-item value="read" text="Replica (Read)"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="1" vs-sm="12" vs-xs="12">
          <vs-button color="primary" class="w-full" @click="loadInstances()">
            <feather-icon icon="SearchIcon"></feather-icon>
          </vs-button>
        </vs-col>
      </vs-row>

      <!-- Instances Table -->
      <vs-table v-if="instances" :data="instances">
        <template slot="thead">
          <vs-th>UUID</vs-th>
          <vs-th>Name</vs-th>
          <vs-th>User</vs-th>
          <vs-th>Plan</vs-th>
          <vs-th>Region</vs-th>
          <vs-th>IP Address</vs-th>
          <vs-th>Role</vs-th>
          <vs-th>Billing</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Created</vs-th>
          <vs-th>Actions</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <small class="text-grey">{{ tr.uuid.substring(0, 8) }}...</small>
            </vs-td>
            <vs-td>{{ tr.name }}</vs-td>
            <vs-td>
              <div>
                <vs-button
                  size="small"
                  color="primary"
                  type="flat"
                  icon="account_box"
                  :to="`/user?id=${tr.user_id}`"
                  style="color: white !important;"
                >
                  {{ tr.user_name }}
                </vs-button>
                <br>
                <small class="text-grey">{{ tr.user_email }}</small>
              </div>
            </vs-td>
            <vs-td>
              <div>
                {{ tr.plan_name }}
                <br>
                <small class="text-grey">
                  {{ tr.vcpus }} vCPU / {{ formatMemory(tr.memory_mb) }} / {{ tr.disk_gb }}GB
                </small>
              </div>
            </vs-td>
            <vs-td>{{ tr.region_name }}</vs-td>
            <vs-td>
              <div v-if="tr.public_ip">
                {{ tr.public_ip }}
                <br>
                <small class="text-grey" v-if="tr.dns_name">{{ tr.dns_name }}</small>
              </div>
              <small v-else class="text-grey">Pending</small>
            </vs-td>
            <vs-td>
              <vs-chip :color="getRoleColor(tr.role)" size="small">
                {{ tr.role }}
              </vs-chip>
              <br>
              <small class="text-grey" v-if="tr.dr_replica_id">Has DR</small>
            </vs-td>
            <vs-td>
              <vs-chip :color="tr.billing_mode === 'hourly' ? 'primary' : 'success'" size="small">
                <feather-icon :icon="tr.billing_mode === 'hourly' ? 'ClockIcon' : 'CalendarIcon'" svgClasses="h-3 w-3 mr-1"></feather-icon>
                {{ tr.billing_mode === 'hourly' ? 'Hourly' : 'Monthly' }}
              </vs-chip>
              <br>
              <small class="text-grey" v-if="tr.billing_mode === 'hourly'">
                {{ formatPrice(tr.price_hourly) }}/hr<br>
                Pending: {{ formatPrice(tr.hourly_cost_accumulated) }}
              </small>
              <small class="text-grey" v-else>
                {{ formatPrice(tr.price_monthly) }}/mo
              </small>
            </vs-td>
            <vs-td>
              <vs-chip :color="getStatusColor(tr.status)" size="small">
                {{ tr.status }}
              </vs-chip>
              <br>
              <vs-chip v-if="tr.health" :color="getHealthColor(tr.health)" size="small">
                {{ tr.health }}
              </vs-chip>
            </vs-td>
            <vs-td>{{ formatDate(tr.created_at) }}</vs-td>
            <vs-td>
              <vs-dropdown vs-custom-content vs-trigger-click>
                <vs-button
                  size="small"
                  type="filled"
                  icon="more_vert"
                ></vs-button>

                <vs-dropdown-menu>
                  <vs-dropdown-item @click="viewDetails(tr)">
                    <feather-icon icon="EyeIcon" class="mr-2" svgClasses="h-4 w-4"></feather-icon>
                    View Details
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="deleteInstance(tr.uuid)">
                    <feather-icon icon="TrashIcon" class="mr-2" svgClasses="h-4 w-4"></feather-icon>
                    <span class="text-danger">Delete Instance</span>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <vs-pagination
        class="my-3"
        :total="pagination.last_page"
        v-model="pagination.current_page"
        @change="loadInstances()"
      ></vs-pagination>
    </vx-card>

    <!-- Details Dialog -->
    <vs-popup
      title="SQL Server Instance Details"
      :active.sync="showDetailsDialog"
    >
      <div class="p-4" v-if="selectedInstance">
        <vs-row>
          <vs-col vs-w="6" class="mb-3">
            <strong>UUID:</strong> {{ selectedInstance.uuid }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Name:</strong> {{ selectedInstance.name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Status:</strong>
            <vs-chip :color="getStatusColor(selectedInstance.status)">
              {{ selectedInstance.status }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Health:</strong>
            <vs-chip :color="getHealthColor(selectedInstance.health)">
              {{ selectedInstance.health || 'Unknown' }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>User:</strong> {{ selectedInstance.user_name }} ({{ selectedInstance.user_email }})
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>User Balance:</strong>
            <vs-chip :color="selectedInstance.user_balance > 100000 ? 'success' : 'warning'">
              {{ formatPrice(selectedInstance.user_balance) }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Plan:</strong> {{ selectedInstance.plan_name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Region:</strong> {{ selectedInstance.region_name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Public IP:</strong> {{ selectedInstance.public_ip || 'N/A' }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>DNS Name:</strong> {{ selectedInstance.dns_name || 'N/A' }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>SQL Version:</strong> {{ selectedInstance.sql_version }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Edition:</strong> {{ selectedInstance.edition }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Role:</strong>
            <vs-chip :color="getRoleColor(selectedInstance.role)">
              {{ selectedInstance.role }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3" v-if="selectedInstance.dr_replica_id">
            <strong>DR Replica:</strong> {{ selectedInstance.dr_replica_id }}
          </vs-col>
          <vs-col vs-w="12" class="mb-3">
            <strong>Billing:</strong>
            <vs-chip :color="selectedInstance.billing_mode === 'hourly' ? 'primary' : 'success'">
              {{ selectedInstance.billing_mode === 'hourly' ? 'Hourly' : 'Monthly' }}
            </vs-chip>
            <span v-if="selectedInstance.billing_mode === 'hourly'" class="ml-2">
              {{ formatPrice(selectedInstance.price_hourly) }}/hour
              (Pending: {{ formatPrice(selectedInstance.hourly_cost_accumulated) }})
            </span>
            <span v-else class="ml-2">
              {{ formatPrice(selectedInstance.price_monthly) }}/month
            </span>
          </vs-col>
          <vs-col vs-w="12" class="mb-3" v-if="selectedInstance.error_message">
            <strong class="text-danger">Error:</strong>
            <p class="text-danger">{{ selectedInstance.error_message }}</p>
            <small class="text-grey">At: {{ formatDate(selectedInstance.error_at) }}</small>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Created:</strong> {{ formatDate(selectedInstance.created_at) }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Last Health Check:</strong> {{ formatDate(selectedInstance.last_health_check_at) }}
          </vs-col>
        </vs-row>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  data() {
    return {
      instances: [],
      stats: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
      },
      filters: {
        instance_id: '',
        user_id: '',
        status: '',
        billing_mode: '',
        role: '',
      },
      showDetailsDialog: false,
      selectedInstance: null,
    }
  },
  mounted() {
    this.loadStats()
    this.loadInstances()
  },
  methods: {
    async loadStats() {
      try {
        const response = await this.$http.get('/admin/managed-sql/stats')
        if (response.data.status) {
          this.stats = response.data.data
        }
      } catch (error) {
        console.error('Failed to load stats:', error)
      }
    },
    async loadInstances() {
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: 20,
          ...this.filters,
        }

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const response = await this.$http.get('/admin/managed-sql/instances', { params })
        if (response.data.status) {
          this.instances = response.data.data.data
          this.pagination = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page,
            total: response.data.data.total,
          }
        }
      } catch (error) {
        console.error('Failed to load instances:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load SQL Server instances',
          color: 'danger',
        })
      }
    },
    viewDetails(instance) {
      this.selectedInstance = instance
      this.showDetailsDialog = true
    },
    async deleteInstance(uuid) {
      const confirmed = await this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete SQL Server Instance',
        text: `Are you sure you want to delete this instance? This action cannot be undone.`,
        acceptText: 'Delete',
      })

      if (confirmed) {
        try {
          await this.$http.delete(`/admin/managed-sql/instances/${uuid}`)
          this.$vs.notify({
            title: 'Success',
            text: 'Instance deletion initiated',
            color: 'success',
          })
          this.loadInstances()
          this.loadStats()
        } catch (error) {
          console.error('Failed to delete instance:', error)
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to delete instance',
            color: 'danger',
          })
        }
      }
    },
    getStatusColor(status) {
      const colors = {
        running: 'success',
        stopped: 'dark',
        provisioning: 'primary',
        starting: 'primary',
        stopping: 'warning',
        suspended: 'warning',
        failed: 'danger',
        deleting: 'danger',
      }
      return colors[status] || 'dark'
    },
    getHealthColor(health) {
      const colors = {
        healthy: 'success',
        degraded: 'warning',
        unhealthy: 'danger',
        unknown: 'dark',
      }
      return colors[health] || 'dark'
    },
    getRoleColor(role) {
      const colors = {
        standalone: 'dark',
        write: 'success',
        read: 'primary',
        transitioning: 'warning',
      }
      return colors[role] || 'dark'
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('fa-IR')
    },
    formatPrice(price) {
      if (!price) return '0'
      return new Intl.NumberFormat('fa-IR').format(price) + ' ریال'
    },
    formatMemory(mb) {
      if (!mb) return 'N/A'
      if (mb >= 1024) {
        return (mb / 1024).toFixed(0) + ' GB'
      }
      return mb + ' MB'
    },
  },
}
</script>

<style scoped>
.vx-card {
  margin-bottom: 1rem;
}
</style>
